import React from 'react'
import AdminSidebar from "../Sidebar/AdminSidebar";
import { NavLink } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SuperAdminApis } from '../../apis/superAdminApi';

const SUB_BADGE: Record<string, string> = {
  basic:   'bg-blue-100 text-blue-800',
  popular: 'bg-purple-100 text-purple-800',
  premium: 'bg-yellow-100 text-yellow-800',
};

const STATUS_BADGE: Record<string, string> = {
  active:    'bg-green-100 text-green-800',
  inactive:  'bg-gray-100  text-gray-600',
  suspended: 'bg-red-100   text-red-800',
};

const getPageFromLink = (label: string, url: string | null): string => {
  if (!isNaN(Number(label))) return label;
  if (url) {
    try { return new URL(url).searchParams.get('page') || '1'; }
    catch { return '1'; }
  }
  return '1';
};

function UsersList() {
  const [userLists, setUserList]       = React.useState<any>({});
  const [searchText, setSearchText]    = React.useState('');
  const [subFilter, setSubFilter]      = React.useState('');
  const [loading, setLoading]          = React.useState(false);
  const [actionModal, setActionModal]  = React.useState<{ open: boolean; user: any; action: 'suspend' | 'activate' } | null>(null);
  const [processing, setProcessing]    = React.useState(false);

  const fetchUsers = React.useCallback((page = '') => {
    setLoading(true);
    SuperAdminApis.getAllUsers(page, { search: searchText }).then(
      (res: any) => {
        if (res?.data) setUserList(res.data?.data ?? res.data);
        setLoading(false);
      }
    ).catch(() => setLoading(false));
  }, [searchText]);

  React.useEffect(() => { fetchUsers(); }, []);

  const handleSearch = () => fetchUsers('');
  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };

  const openActionModal = (user: any, action: 'suspend' | 'activate') =>
    setActionModal({ open: true, user, action });

  const closeActionModal = () => setActionModal(null);

  const confirmAction = () => {
    if (!actionModal) return;
    const { user, action } = actionModal;
    setProcessing(true);

    SuperAdminApis.updateUserStatus(user.id, { status: action === 'suspend' ? 'suspended' : 'active' }).then(
      (res: any) => {
        if (res?.data) {
          toast.success(`User ${action === 'suspend' ? 'suspended' : 'activated'} successfully`);
          fetchUsers('');
        } else {
          toast.error(res?.response?.data?.message || 'Action failed');
        }
        setProcessing(false);
        closeActionModal();
      }
    ).catch((err: any) => {
      toast.error(err?.response?.data?.message || 'Action failed');
      setProcessing(false);
      closeActionModal();
    });
  };

  const isSubExpired = (subEnd: string) => subEnd && new Date(subEnd) < new Date();

  const allUsers: any[] = userLists?.data ?? [];
  const filtered = allUsers.filter((u: any) => u.role !== 'admin' && (!subFilter || u.sub_type === subFilter));

  return (
    <>
      <AdminSidebar />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="relative md:ml-64 bg-gray-50 min-h-screen">
        <div className="py-8 lg:py-12 lg:px-10 px-6">

          {/* Header */}
          <div className="flex flex-wrap items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Users</h1>
              <p className="text-sm text-gray-500 mt-1">Manage all registered users</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-600">
              Total: <span className="font-bold text-gray-800">{userLists?.total ?? 0}</span>
            </div>
          </div>

          {/* Search + filter bar */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                <path fill="currentColor" d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0a5.5 5.5 0 0 1 11 0z"/>
              </svg>
              <input
                type="text"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search name, email or phone…"
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 w-72 focus:outline-none focus:ring-2 focus:ring-[#0071BC]"
              />
            </div>

            <select
              value={subFilter}
              onChange={e => setSubFilter(e.target.value)}
              className="border border-gray-300 text-gray-600 text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0071BC]"
            >
              <option value="">All Plans</option>
              <option value="basic">Basic</option>
              <option value="popular">Popular</option>
              <option value="premium">Premium</option>
            </select>

            <button
              onClick={handleSearch}
              disabled={loading}
              className="bg-[#0071BC] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Search'}
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="text-xs uppercase bg-gray-50 text-gray-500 border-b">
                  <tr>
                    <th className="px-6 py-4">#</th>
                    <th className="px-6 py-4">User</th>
                    <th className="px-6 py-4">Phone</th>
                    <th className="px-6 py-4">Plan</th>
                    <th className="px-6 py-4">Subscription</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-16 text-center text-gray-400">
                        <div className="flex justify-center items-center gap-2">
                          <div className="w-5 h-5 border-2 border-[#0071BC] border-t-transparent rounded-full animate-spin"></div>
                          Loading users…
                        </div>
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-16 text-center text-gray-400">
                        <div className="flex flex-col items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                          </svg>
                          <p className="font-medium">No users found</p>
                          {(searchText || subFilter) && <p className="text-xs">Try adjusting your search or filter</p>}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((user: any, idx: number) => {
                      const expired = isSubExpired(user.sub_end);
                      const subStatus = user.sub_status === 'active' && !expired ? 'active' : user.sub_status === 'active' && expired ? 'inactive' : user.sub_status ?? 'inactive';

                      return (
                        <tr key={user.id} className="border-b hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-gray-400">{idx + 1}</td>

                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-800">{user.name}</div>
                            <div className="text-xs text-gray-400">{user.email}</div>
                          </td>

                          <td className="px-6 py-4 text-gray-600">{user.phone_number || '—'}</td>

                          <td className="px-6 py-4">
                            {user.sub_type ? (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${SUB_BADGE[user.sub_type] ?? 'bg-gray-100 text-gray-600'}`}>
                                {user.sub_type}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">Free</span>
                            )}
                          </td>

                          <td className="px-6 py-4">
                            {user.sub_start ? (
                              <div>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mb-1 ${STATUS_BADGE[subStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                                  {expired && user.sub_status === 'active' ? 'Expired' : subStatus}
                                </span>
                                <div className="text-xs text-gray-400">
                                  {user.sub_start} → {user.sub_end}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">No subscription</span>
                            )}
                          </td>

                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[user.status ?? 'active'] ?? 'bg-gray-100 text-gray-600'}`}>
                              {user.status ?? 'active'}
                            </span>
                          </td>

                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <NavLink
                                to={`/user-details/${user.id}`}
                                className="text-[#0071BC] hover:underline text-xs font-medium"
                              >
                                View
                              </NavLink>

                              {user.status === 'suspended' ? (
                                <button
                                  onClick={() => openActionModal(user, 'activate')}
                                  className="text-green-600 hover:underline text-xs font-medium"
                                >
                                  Activate
                                </button>
                              ) : (
                                <button
                                  onClick={() => openActionModal(user, 'suspend')}
                                  className="text-red-500 hover:underline text-xs font-medium"
                                >
                                  Suspend
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {userLists?.links && userLists.links.length > 3 && (
              <div className="px-6 py-4 border-t flex items-center gap-1">
                {userLists.links.map((link: any, idx: number) => (
                  <button
                    key={idx}
                    onClick={() => fetchUsers(getPageFromLink(link.label, link.url))}
                    disabled={!link.url || link.active || loading}
                    className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                      link.active
                        ? 'bg-[#0071BC] text-white'
                        : link.url
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {actionModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-2">
              {actionModal.action === 'suspend' ? 'Suspend User' : 'Activate User'}
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to{' '}
              <span className={actionModal.action === 'suspend' ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                {actionModal.action}
              </span>{' '}
              <span className="font-semibold text-gray-700">{actionModal.user.name}</span>?
              {actionModal.action === 'suspend' && ' They will lose access to the platform.'}
            </p>
            <div className="flex gap-3">
              <button onClick={closeActionModal} disabled={processing} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={confirmAction}
                disabled={processing}
                className={`flex-1 px-4 py-2.5 rounded-lg text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2 ${
                  actionModal.action === 'suspend' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {processing && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                {processing ? 'Processing…' : actionModal.action === 'suspend' ? 'Suspend' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UsersList;
